/**
 * Referral-code capture + persistence.
 *
 * A referral code can arrive two ways:
 *   - `?ref=CODE` on any URL  → captured by captureReferralCode() in Layout
 *   - `/r/CODE` landing page  → stored by the ReferralLanding page
 *
 * The code is held in localStorage until the post-signup track-signup call
 * consumes it (see AuthContext), then cleared.
 */

import { REFERRAL_QUERY_PARAM, REFERRAL_STORAGE_KEY } from "@/config/referral";

/** Persist a referral code (normalized) for the signup flow to pick up. */
export function storeReferralCode(code: string): void {
  try {
    const normalized = code.trim().toUpperCase();
    if (normalized) localStorage.setItem(REFERRAL_STORAGE_KEY, normalized);
  } catch {
    // ignore localStorage failures (private mode, etc.)
  }
}

/** Read the pending referral code, if any. */
export function getStoredReferralCode(): string | null {
  try {
    return localStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Clear the pending referral code once signup has consumed it. */
export function clearStoredReferralCode(): void {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Capture a `?ref=CODE` query param into localStorage. Called once on app load. */
export function captureReferralCode(): void {
  try {
    const code = new URLSearchParams(window.location.search).get(
      REFERRAL_QUERY_PARAM,
    );
    if (code) storeReferralCode(code);
  } catch {
    // ignore
  }
}
